/**
 * Calendar Service
 * Business logic for calendar operations
 */

import { recipeApiService } from './recipeApiService'
import { sessionApiService } from './sessionApiService'
import { clientLogger } from '../utils/clientLogger'
import { RecipeRecord } from '../types/recipe'
import {
  CalendarData,
  DateRange,
  MealType,
  AssignmentMap,
  MealAssignment,
  SessionMealTypePreference
} from '../types/calendar'
import {
  getAssignmentKey,
  formatDateKey,
  isDateEditable as utilIsDateEditable
} from '../utils/calendarUtils'

export class CalendarService {
  /**
   * Load all data needed for calendar view
   * Fetches recipes, assignments, and preferences in parallel
   */
  async getCalendarData(
    userId: string,
    sessionId: string | null,
    dateRange: DateRange
  ): Promise<CalendarData> {
    try {
      const startDateStr = formatDateKey(dateRange.start)
      const endDateStr = formatDateKey(dateRange.end)

      // Fetch data in parallel for performance
      const [datedRecipes, unassignedRecipes, sessionPrefs] = await Promise.all([
        // Load recipes with assigned dates in the visible range
        recipeApiService.getRecipesByDateRange(userId, startDateStr, endDateStr),

        // Load unassigned recipes from recent sessions (last 7 days)
        this.getRecentUnassignedRecipes(userId),

        // Load session preferences (if session exists)
        sessionId ? this.getSessionPreferences(userId) : Promise.resolve(null)
      ])

      // Parse assignments from dated recipes
      const assignments = this.parseAssignments(datedRecipes)

      // Combine all recipes (assigned + unassigned)
      const allRecipes = [...datedRecipes, ...unassignedRecipes]

      // Extract enabled meal types from preferences
      const enabledMealTypes = this.extractEnabledMealTypes(sessionPrefs)

      return {
        assignments,
        recipes: allRecipes,
        unassignedRecipes,
        enabledMealTypes
      }
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to load calendar data')
      throw error
    }
  }

  /**
   * Get unassigned recipes from sessions created in the last 7 days
   * This allows users to see their recent meal plans in the recipe bank
   */
  async getRecentUnassignedRecipes(userId: string): Promise<RecipeRecord[]> {
    try {
      const oneWeekAgo = new Date()
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
      const sinceDateStr = formatDateKey(oneWeekAgo)

      // Use new endpoint to get unassigned recipes from recent sessions
      return await recipeApiService.getUnassignedRecipesSince(userId, sinceDateStr)
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to load recent unassigned recipes')
      // Fallback: try to get from active session
      try {
        const session = await sessionApiService.getActiveSession(userId)
        if (session?.id) {
          return await recipeApiService.getUnassignedRecipesBySession(userId, session.id)
        }
      } catch (fallbackError) {
        clientLogger.warn({ err: fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError)) }, 'Fallback also failed')
      }
      return [] // Return empty array instead of failing the whole calendar
    }
  }

  /**
   * Get session preferences for meal types
   */
  private async getSessionPreferences(userId: string): Promise<any> {
    try {
      const session = await sessionApiService.getActiveSession(userId)
      return session?.session_preferences || null
    } catch (error) {
      clientLogger.warn({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to load session preferences')
      return null
    }
  }

  /**
   * Parse recipe records into assignment map
   * Converts database records into typed MealAssignment objects
   */
  parseAssignments(recipes: RecipeRecord[]): AssignmentMap {
    const map = new Map<string, MealAssignment>()

    recipes.forEach(recipe => {
      // Skip recipes without assigned dates
      if (!recipe.assigned_dates || recipe.assigned_dates.length === 0) {
        return
      }

      // Create an assignment for each date this recipe is assigned to
      recipe.assigned_dates.forEach((dateStr: string) => {
        const key = getAssignmentKey(dateStr, recipe.recipe_data.mealType)

        const assignment: MealAssignment = {
          id: `${recipe.id}_${dateStr}`, // Composite ID for uniqueness
          recipeId: recipe.id,
          recipeName: recipe.recipe_data.name,
          mealType: recipe.recipe_data.mealType,
          date: dateStr,
          createdAt: recipe.created_at,
          updatedAt: recipe.updated_at
        }

        map.set(key, assignment)
      })
    })

    return map
  }

  /**
   * Extract enabled meal types from session preferences
   * Falls back to all meal types if no preferences found
   */
  extractEnabledMealTypes(sessionPrefs: any): MealType[] {
    if (!sessionPrefs?.mealTypes) {
      // Default: all meal types enabled
      return ['breakfast', 'lunch', 'dinner', 'snacks', 'dessert']
    }

    // Filter to only enabled meal types
    const enabled = sessionPrefs.mealTypes
      .filter((mt: SessionMealTypePreference) => mt.enabled)
      .map((mt: SessionMealTypePreference) => mt.type)

    // If none enabled, return defaults
    return enabled.length > 0
      ? enabled
      : ['breakfast', 'lunch', 'dinner', 'snacks', 'dessert']
  }

  /**
   * Validate that a date is editable based on business rules
   * - 1 week prior: editable
   * - Current date forward: editable
   * - 3 months ahead: editable
   * - Beyond that: read-only
   */
  isDateEditable(date: Date): boolean {
    return utilIsDateEditable(date)
  }

  /**
   * Calculate the date range for loading calendar data
   * Loads 1 month before and 4 months after current month
   */
  getLoadDateRange(): DateRange {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Start from 1 month before current month
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)

    // End at 4 months after current month (buffer for pre-loading)
    const end = new Date(today.getFullYear(), today.getMonth() + 4, 0)

    return { start, end }
  }

  /**
   * Get all dates a specific recipe is assigned to
   * Useful for UI indicators and validation
   */
  getRecipeAssignedDates(
    assignments: AssignmentMap,
    recipeId: string
  ): string[] {
    const dates: string[] = []

    for (const assignment of assignments.values()) {
      if (assignment.recipeId === recipeId) {
        dates.push(assignment.date)
      }
    }

    return dates.sort() // Sort chronologically
  }

  /**
   * Check if a recipe is already assigned to any date
   */
  isRecipeAssigned(assignments: AssignmentMap, recipeId: string): boolean {
    for (const assignment of assignments.values()) {
      if (assignment.recipeId === recipeId) {
        return true
      }
    }
    return false
  }

  /**
   * Group unassigned recipes by meal type
   * Returns a map of meal type -> recipes
   */
  groupRecipesByMealType(recipes: RecipeRecord[]): Record<MealType, RecipeRecord[]> {
    const grouped: Record<string, RecipeRecord[]> = {
      breakfast: [],
      lunch: [],
      dinner: [],
      snacks: [],
      dessert: []
    }

    recipes.forEach(recipe => {
      const mealType = recipe.recipe_data.mealType
      if (grouped[mealType]) {
        grouped[mealType].push(recipe)
      }
    })

    return grouped as Record<MealType, RecipeRecord[]>
  }

  /**
   * Filter out assigned recipes from a list
   * Used to build the recipe bank (only unassigned recipes)
   */
  filterUnassignedRecipes(
    recipes: RecipeRecord[],
    assignments: AssignmentMap
  ): RecipeRecord[] {
    return recipes.filter(recipe => !this.isRecipeAssigned(assignments, recipe.id))
  }
}

// Export singleton instance
export const calendarService = new CalendarService()
export default calendarService
